import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import type { ItemDto, ItemUnit, MenuDto, MenuKind } from '@tiffinify/contracts';
import { addDays, formatDay, mealsOf, orderLineText, type Meal } from '@tiffinify/core';
import { LoadError, PageLoader } from '../components/ui';
import { Amt, Av, Back, Badge, Bar, Btn, Btn2, Card, Chip, Chips, Empty, Ends, Field, G2, Group, Hero, Ic, Kick, Mk, Note, Row, Screen, Seg, Sheet, SheetHead, Sm, Sp, Title, Top } from '../components/p';
import { ScheduleField } from '../components/ScheduleField';
import { ApiClientError, post, put } from '../lib/api';
import { errorMessage, formatInr, mealName } from '../lib/format';
import { useItems, useMe, useMenu, useMenus } from '../lib/queries';

export const KIND: Record<MenuKind, string> = { festival: 'Festival', everyday: 'Every day', quick: 'Quick sale', nextday: 'Next-day dinner', stock: 'Ready stock' };
/** Links are built on this site's own address, so they work on staging and production alike. */
export const here = (link: string) => { try { return `${window.location.origin}${new URL(link).pathname}`; } catch { return link; } };
export const closesText = (closesAt: string | null) => {
  if (!closesAt) return 'Open until you close it';
  const [d, t] = closesAt.split('T');
  const [h, m] = (t ?? '00:00').split(':').map(Number);
  const time = `${((h ?? 0) % 12) || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${(h ?? 0) < 12 ? 'am' : 'pm'}`;
  return `Closes ${formatDay(d ?? '')}, ${time}`;
};
export const daysText = (days: string[]) => days.length > 3 ? `${formatDay(days[0]!)} – ${formatDay(days[days.length - 1]!)} · ${days.length} days` : days.map((d) => formatDay(d)).join(' or ');
export const weight = (g: number) => orderLineText({ name: '', unit: 'kg', qty: g }).trim();
const copy = async (text: string, done: (msg: string) => void) => {
  try { await navigator.clipboard.writeText(text); done('Copied.'); } catch { done('Press and hold to copy it.'); }
};

/** The Menus view of the Menus tab (P11): the open festival up top, the rest below, the kitchen's own page. */
export function MenusView() {
  const q = useMenus();
  const d = q.data;
  const [note, setNote] = useState('');
  if (q.isError && !d) return <LoadError what="your Menus" onRetry={() => void q.refetch()} />;
  if (!d) return <PageLoader label="Loading your Menus" />;
  const [lead, ...rest] = d.menus;
  return (
    <>
      {!lead ? (
        <Card><Empty title="No Menus yet">A Menu is a link your customers order from: a festival, a quick sale or every day.</Empty></Card>
      ) : (
        <Hero to={`/menus/${lead.id}`} label={lead.name}>
          <div className="p-sec"><Kick>{KIND[lead.kind]} · {lead.status === 'open' ? 'open' : 'closed'}</Kick><Kick style={{ color: '#B0A79A' }}>{closesText(lead.closesAt)}</Kick></div>
          <b style={{ fontSize: 20, letterSpacing: '-0.02em' }}>{lead.name}</b>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}><span className="p-big" style={{ fontSize: 52 }}>{lead.orders}</span><span className="p-muted">{lead.orders === 1 ? 'order' : 'orders'} · {formatInr(lead.total)}</span></div>
          <span className="p-muted">Delivery {daysText(lead.days)}</span>
        </Hero>
      )}
      {rest.length > 0 && (
        <Card>
          {rest.map((m) => (
            <Row key={m.id} to={`/menus/${m.id}`} left={<Ic icon={m.kind === 'everyday' ? 'refresh' : 'receipt'} tone={m.status === 'open' ? 's' : ''} />} t={m.name}
              s={`${m.orders} ${m.orders === 1 ? 'order' : 'orders'} · ${formatInr(m.total)} · ${m.status === 'open' ? closesText(m.closesAt) : 'closed'}`}
              right={<Badge tone={m.status === 'open' ? 'g' : ''}>{m.status === 'open' ? 'Open' : 'Closed'}</Badge>} />
          ))}
        </Card>
      )}
      <Card>
        <Row left={<Ic icon="link" tone="s" />} t="Your kitchen page" s={here(d.kitchenLink).replace(/^https?:\/\//, '')} right={<Sm label="Copy" onClick={() => void copy(here(d.kitchenLink), setNote)} />} />
      </Card>
      {note && <span className="p-status" role="status">{note}</span>}
    </>
  );
}

type Picked = { itemId: string; stock: string };

/** New Menu (P12), with its rules (P14) on the same page. With an id, it changes that Menu. */
export function NewMenu() {
  const { id = '' } = useParams();
  const editing = !!id;
  // Run again (P19): a new Menu with an old one's items and rules, on new dates.
  const from = useSearchParams()[0].get('from') ?? '';
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe().data;
  const served = me ? mealsOf(me.kitchen.meals) : [];
  const today = me?.today ?? '';
  const items = useItems().data?.items ?? [];
  const menuQuery = useMenu(id || from);
  const existing = menuQuery.data?.menu;

  const [section, setSection] = useState<'items' | 'rules' | null>(null);
  const [kind, setKind] = useState<MenuKind>('festival');
  const [name, setName] = useState('');
  const [repeats, setRepeats] = useState(true);
  const [reviewAfter, setReviewAfter] = useState('');
  const [closeDay, setCloseDay] = useState('');
  const [closeTime, setCloseTime] = useState('');
  const closesAt = closeDay && closeTime ? closeDay + 'T' + closeTime : '';
  const setClosesAt = (value: string) => {
    setCloseDay(value.slice(0, 10));
    setCloseTime(value.slice(11, 16));
  };
  const [days, setDays] = useState<string[]>([]);
  const [dayInput, setDayInput] = useState('');
  const [meal, setMeal] = useState<Meal | undefined>(undefined);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [minTotal, setMinTotal] = useState('');
  const [charge, setCharge] = useState('');
  const [freeAbove, setFreeAbove] = useState('');
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState<{ name: string; price: string; unit: ItemUnit }>({ name: '', price: '', unit: 'kg' });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!existing || loaded) return;
    setRepeats(existing.repeats); setReviewAfter(existing.reviewAfter ?? ''); setKind(existing.kind); setName(existing.name); setMeal(existing.meal);
    if (editing) { setClosesAt(existing.closesAt ?? ''); setDays(existing.days); }
    setPicked(existing.items.map((i) => ({ itemId: i.id, stock: i.stock === null ? '' : String(i.unit === 'kg' ? i.stock / 1000 : i.stock) })));
    setMinTotal(existing.minTotal ? String(existing.minTotal) : ''); setCharge(existing.deliveryCharge ? String(existing.deliveryCharge) : '');
    setFreeAbove(existing.freeAbove ? String(existing.freeAbove) : ''); setNote(existing.note); setLoaded(true);
  }, [existing, loaded]);

  // Sensible first values: tomorrow for delivery, closing the evening before.
  useEffect(() => {
    if (editing || !today || days.length) return;
    if (kind === 'everyday' || kind === 'stock') { setDays([today]); return; }
    // Booking closes at 9 pm the evening before delivery; if that has passed already, both move a day.
    const now = new Date();
    const nowLocal = `${today}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    let first = addDays(today, 1);
    if (`${addDays(first, -1)}T21:00` <= nowLocal) first = addDays(first, 1);
    setDays([first]);
    if (!closesAt) setClosesAt(`${addDays(first, -1)}T21:00`);
  }, [today, editing, kind, days.length, closesAt]);

  const unavailableItems = existing?.items.filter((item) =>
    picked.some((entry) => entry.itemId === item.id) && !items.some((available) => available.id === item.id)) ?? [];

  const defaultMeal: Meal = served.includes('lunch') ? 'lunch' : served[0] ?? 'lunch';
  const toggle = (it: ItemDto) => setPicked((p) => (p.some((x) => x.itemId === it.id) ? p.filter((x) => x.itemId !== it.id) : [...p, { itemId: it.id, stock: '' }]));
  const setStock = (itemId: string, v: string) => setPicked((p) => p.map((x) => (x.itemId === itemId ? { ...x, stock: v.replace(/[^\d.]/g, '') } : x)));
  const addItem = useMutation({
    mutationFn: () => post<ItemDto>('/items', { name: newItem.name, unit: newItem.unit, price: Number(newItem.price) || 0 }),
    onSuccess: (it) => { void qc.invalidateQueries({ queryKey: ['items'] }); setPicked((p) => [...p, { itemId: it.id, stock: '' }]); setAdding(false); setNewItem({ name: '', price: '', unit: 'kg' }); },
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name, kind, repeats: kind === 'everyday' && repeats, reviewAfter: reviewAfter || null, closesAt: kind === 'everyday' && repeats ? null : closesAt || null, days: kind === 'everyday' && repeats ? [today] : days, meal: meal ?? defaultMeal,
        minTotal: Number(minTotal) || 0, deliveryCharge: Number(charge) || 0, freeAbove: Number(freeAbove) || 0, note,
        items: picked.map((p) => {
          const it = items.find((i) => i.id === p.itemId);
          const n = p.stock === '' ? null : Number(p.stock);
          return { itemId: p.itemId, stock: n === null || Number.isNaN(n) ? null : it?.unit === 'kg' ? Math.round(n * 1000) : Math.round(n) };
        }),
      };
      return editing ? put<MenuDto>(`/menus/${id}`, body) : post<MenuDto>('/menus', body);
    },
    onSuccess: (m) => { void qc.invalidateQueries(); nav(`/menus/${m.id}${editing ? '' : '?tab=share'}`, { replace: editing }); },
  });

  const fields = save.error instanceof ApiClientError ? save.error.fields : {};

  if ((editing || from) && menuQuery.isError) {
    return <Screen><Top><Back to="/menus?view=menus" label="Menus" /></Top><LoadError what="this Menu" onRetry={() => void menuQuery.refetch()} /></Screen>;
  }
  if ((editing || from) && !loaded) {
    return <Screen><Top><Back to="/menus?view=menus" label="Menus" /></Top><PageLoader label="Opening the Menu" /></Screen>;
  }

  if (section) {
    return (
      <Screen gap={16}>
        <Top><Back label={name || 'Menu'} onClick={() => setSection(null)} /></Top>
        <Title h={section === 'items' ? 'Items' : 'Rules'} sub={section === 'items' ? 'Choose the items and optional stock limits.' : 'Set how customers order from this Menu.'} />
        {section === 'items' ? (
          <>
            <Group label="Items" right={<button type="button" className="p-link" onClick={() => setAdding(!adding)}>{adding ? 'Close' : '+ Add an item'}</button>}>
              {items.length === 0 && !adding && <Note>Add what this Menu sells: by weight (price per kg), a pack, a plate or a gift box.</Note>}
              {items.length > 0 && (
                <Card>
                  {items.map((it) => {
                    const p = picked.find((x) => x.itemId === it.id);
                    return (
                      <Row key={it.id} left={<Mk food={it.food} />} t={it.name} s={`${formatInr(it.price)}${it.unit === 'kg' ? ' / kg' : ` a ${it.unit}`}${p ? ' · limit (optional)' : ''}`}
                        right={p ? (
                          <span className="p-pair">
                            <input aria-label={`${it.name} limit`} className="p-mini" value={p.stock} placeholder={it.unit === 'kg' ? 'kg' : 'qty'} inputMode="decimal" onChange={(e) => setStock(it.id, e.target.value)} />
                            <Sm label="Added" k onClick={() => toggle(it)} />
                          </span>
                        ) : <Sm label="Add" onClick={() => toggle(it)} />} />
                    );
                  })}
                </Card>
              )}
              {unavailableItems.length > 0 && (
                <Card>{unavailableItems.map((item) => (
                  <Row key={item.id} t={item.name} s="No longer for sale. Remove it before saving this Menu."
                    right={<Sm label="Remove" onClick={() => setPicked((entries) => entries.filter((entry) => entry.itemId !== item.id))} />} />
                ))}</Card>
              )}
              {adding && (
                <Card pad style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <G2>
                    <Field label="Item" id="mi-name" value={newItem.name} placeholder="Boondi laddu" onChange={(v) => setNewItem({ ...newItem, name: v })} />
                    <Field label={newItem.unit === 'kg' ? 'Price per kg' : 'Price'} id="mi-price" value={newItem.price} inputMode="numeric" placeholder="₹" onChange={(v) => setNewItem({ ...newItem, price: v.replace(/\D/g, '') })} />
                  </G2>
                  <Seg label="Sold as" value={newItem.unit} onChange={(u) => setNewItem({ ...newItem, unit: u })}
                    options={[{ value: 'kg', label: 'By weight' }, { value: 'pack', label: 'Pack' }, { value: 'box', label: 'Box' }, { value: 'plate', label: 'Plate' }]} />
                  {addItem.error && <span className="p-error" role="alert">{errorMessage(addItem.error)}</span>}
                  <Btn2 label={addItem.isPending ? 'Saving…' : 'Save item'} icon="check" disabled={!newItem.name.trim() || addItem.isPending} onClick={() => addItem.mutate()} />
                </Card>
              )}
            </Group>
          </>
        ) : (
          <>

            <Group label="All optional">
              <G2>
                <Field label="Smallest order ₹" id="m-min" value={minTotal} inputMode="numeric" placeholder="0" onChange={(v) => setMinTotal(v.replace(/\D/g, ''))} />
                <Field label="Delivery charge ₹" id="m-charge" value={charge} inputMode="numeric" placeholder="0" onChange={(v) => setCharge(v.replace(/\D/g, ''))} />
              </G2>
              <Field label="Free delivery above ₹" id="m-free" value={freeAbove} inputMode="numeric" placeholder="0" onChange={(v) => setFreeAbove(v.replace(/\D/g, ''))} />
            </Group>
            <Field label="A line for customers (optional)" id="m-note" value={note} placeholder="Made fresh, delivered to your door" onChange={setNote} />
            <ScheduleField label="Review same-day orders after (optional)" mode="time" id="m-review" value={reviewAfter} onChange={setReviewAfter} clearable />

          </>
        )}
        <Btn label={section === 'items' ? 'Use these items' : 'Save rules'} go="check" onClick={() => setSection(null)} />
      </Screen>
    );
  }

  return (
    <Screen gap={16}>
      <Top><Back to={editing ? `/menus/${id}` : '/menus?view=menus'} label="Cancel" /><Sp /></Top>
      <Title h={editing ? 'Change Menu' : from ? 'Run again' : 'New Menu'} sub={from && !editing ? 'Same items and rules. Pick the new name and dates.' : 'Customers order from its link.'} />
      <Chips label="Kind of Menu">{(Object.keys(KIND) as MenuKind[]).map((k) => <Chip key={k} on={kind === k} onClick={() => setKind(k)}>{KIND[k]}</Chip>)}</Chips>
      <Field label="Name" id="m-name" value={name} placeholder="Deepavali sweets and savouries" onChange={setName} error={fields.name} />
      {kind === 'everyday' && <Group label="Delivery schedule">
        <Seg label="Delivery schedule" value={repeats ? 'rolling' : 'dates'} onChange={(v) => setRepeats(v === 'rolling')}
          options={[{value:'rolling',label:'Kitchen days'},{value:'dates',label:'Specific dates'}]} />
        {repeats && <Note icon="calendar">Customers can book the next 14 days. Kitchen days off and holidays are skipped.</Note>}
      </Group>}
      {!(kind === 'everyday' && repeats) && <div className="p-menu-dates">
        <G2>
          <ScheduleField label="Closing date" id="m-close-day" value={closeDay} min={today} today={today}
            onChange={(value) => { setCloseDay(value); if (!value) setCloseTime(''); save.reset(); }} clearable error={fields.closesAt} />
          <ScheduleField label="Closing time" id="m-close-time" mode="time" value={closeTime}
            onChange={(value) => { setCloseTime(value); save.reset(); }} clearable />
        </G2>
        {!!closeDay !== !!closeTime && <span className="p-error" role="alert">Choose both a closing date and time, or clear both to keep booking open.</span>}
        <ScheduleField label="Add a delivery day" id="m-day" value={dayInput} min={today} today={today}
          onChange={(value) => { setDayInput(''); if (value && !days.includes(value)) setDays([...days, value].sort()); }} />
      </div>}
      {!(kind === 'everyday' && repeats) && days.length > 0 && <Chips label="Delivery days">{days.map((d) => <Chip key={d} on onClick={() => setDays(days.filter((x) => x !== d))}>{formatDay(d)} ✕</Chip>)}</Chips>}
      {served.length > 1 && (kind === 'everyday' || kind === 'nextday') && <Seg label="Meal" value={meal ?? defaultMeal} onChange={setMeal} options={served.map((m) => ({ value: m, label: mealName(m) }))} />}

      <Card>
        <Row left={<Ic icon="receipt" tone="s" />} t="Items"
          s={picked.length ? `${picked.length} chosen · ${picked.map((entry) => items.find((item) => item.id === entry.itemId)?.name ?? 'Unavailable item').slice(0, 3).join(', ')}` : 'Choose what customers can order'}
          onClick={() => setSection('items')} chev />
        <Row left={<Ic icon="sliders" />} t="Rules"
          s={[`Minimum ${formatInr(Number(minTotal) || 0)}`, `delivery ${formatInr(Number(charge) || 0)}`].join(' · ')}
          onClick={() => setSection('rules')} chev />
      </Card>

      {save.error && <span className="p-error" role="alert">{errorMessage(save.error)}</span>}
      <Btn label={editing ? 'Save changes' : 'Open and share'} go="send" disabled={!name.trim() || days.length === 0 || picked.length === 0 || (!(kind === 'everyday' && repeats) && !!closeDay !== !!closeTime)} busy={save.isPending} onClick={() => save.mutate()} />
    </Screen>
  );
}

/** A Menu's own page (P15–P18): its numbers, then Orders, Cook, Pack and Share. */
export function MenuPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (['orders', 'cook', 'pack', 'money', 'share'] as const).find((t) => t === params.get('tab')) ?? 'orders';
  const qc = useQueryClient();
  const q = useMenu(id);
  const [sticker, setSticker] = useState(false);
  const [note, setNote] = useState('');
  const openClose = useMutation({ mutationFn: (open: boolean) => put(`/menus/${id}/open`, { open }), onSuccess: () => void qc.invalidateQueries() });
  if (q.isError && !q.data) return <Screen><Top><Back to="/menus?view=menus" label="Menus" /></Top><LoadError what="this Menu" onRetry={() => void q.refetch()} /></Screen>;
  if (!q.data) return <Screen><Top><Back to="/menus?view=menus" label="Menus" /></Top><PageLoader label="Opening the Menu" /></Screen>;
  const { menu: m, orders } = q.data;
  const live = orders.filter((o) => o.status !== 'cancelled');
  const unpaid = live.filter((o) => o.payState === 'unpaid' || o.payState === 'says_paid').length;
  const collected = live.filter((o) => o.payState === 'paid').reduce((sum, o) => sum + o.total, 0);
  const outstanding = live.filter((o) => o.payState === 'unpaid' || o.payState === 'says_paid');
  const link = here(m.link);
  const postText = `${m.name} is open${m.note ? `: ${m.note}` : ''}.\n${m.closesAt ? `${closesText(m.closesAt)}. ` : ''}Delivery ${daysText(m.days)}.\nOrder here: ${link}`;
  const pick = (t: typeof tab) => setParams((p) => { const n = new URLSearchParams(p); if (t === 'orders') n.delete('tab'); else n.set('tab', t); return n; }, { replace: true });

  const totals = new Map<string, { name: string; unit: ItemUnit; qty: number }>();
  const ready = live.filter((order) => order.status !== 'waiting');
  for (const l of ready.flatMap((o) => o.lines)) {
    const key = `${l.name}|${l.unit}`;
    const t = totals.get(key) ?? { name: l.name, unit: l.unit, qty: 0 };
    t.qty += l.qty; totals.set(key, t);
  }
  const grams = [...totals.values()].filter((t) => t.unit === 'kg').reduce((n, t) => n + t.qty, 0);

  const portions = new Map<string, Map<number, number>>();
  for (const line of ready.flatMap((order) => order.lines)) {
    if (line.unit !== 'kg') continue;
    const sizes = portions.get(line.name) ?? new Map<number, number>();
    sizes.set(line.qty, (sizes.get(line.qty) ?? 0) + 1);
    portions.set(line.name, sizes);
  }

  return (
    <Screen gap={16}>
      <Top><Back to="/menus?view=menus" label="Menus" /><Sp /></Top>
      <Title h={m.name} badge={<Badge tone={m.status === 'open' ? 'g' : ''}>{m.status === 'open' ? 'Open' : 'Closed'}</Badge>}
        sub={`${closesText(m.closesAt)} · delivery ${daysText(m.days)}`} />
      <div className="p-links" style={{ justifyContent: 'flex-start' }}>
        <button type="button" onClick={() => pick('share')}>Share Menu</button>
        <Link to={`/menus/${m.id}/edit`}>Change Menu and rules</Link>
        <Link to={`/menus/new?from=${m.id}`}>Run again</Link>
        <button type="button" className={m.status === 'open' ? 'r' : undefined} onClick={() => openClose.mutate(m.status !== 'open')}>{m.status === 'open' ? 'Close booking' : 'Open again'}</button>
      </div>
      <Card pad style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', textAlign: 'center', padding: '16px 6px' }}>
        <div><div className="p-mid" style={{ fontSize: 26 }}>{live.length}</div><Kick>orders</Kick></div>
        <div style={{ borderLeft: '1px solid #F2EDE6', borderRight: '1px solid #F2EDE6' }}><div className="p-mid" style={{ fontSize: 26 }}>{formatInr(live.reduce((n, o) => n + o.total, 0))}</div><Kick>booked</Kick></div>
        <div><div className="p-mid" style={{ fontSize: 26, color: unpaid ? '#B03E27' : undefined }}>{unpaid}</div><Kick>unpaid</Kick></div>
      </Card>
      <Seg label="Menu sections" value={tab} onChange={pick} options={[{ value: 'orders', label: 'Orders' }, { value: 'cook', label: 'Cook' }, { value: 'pack', label: 'Pack' }, { value: 'money', label: 'Money' }]} />

      {tab === 'orders' && (orders.length === 0
        ? <Card><Empty title="No orders yet">Share the link: orders arrive here complete, with the address and the total.</Empty></Card>
        : <Card>{orders.map((o) => (
            <Row key={o.id} to={`/orders/${o.id}`} style={o.status === 'cancelled' ? { opacity: 0.5 } : undefined} left={<Av name={o.name} />} t={`${o.name} · #${o.number}`}
              s={[formatDay(o.day), o.lines.map(orderLineText).join(' · ')].join(' · ')}
              right={<Ends amt={formatInr(o.total)} badge={o.status === 'cancelled' ? <Badge>Cancelled</Badge> : <Badge tone={o.payState === 'paid' ? 'g' : o.payState === 'says_paid' ? 's' : 'r'}>{o.payState === 'paid' ? 'Paid' : o.payState === 'says_paid' ? 'Says paid' : 'Unpaid'}</Badge>} />} />
          ))}</Card>)}

      {tab === 'cook' && (
        <>
          {grams > 0 && <Hero label="To make"><Kick>To make so far</Kick><span className="p-big" style={{ fontSize: 52 }}>{weight(grams)}</span><span className="p-muted">{live.length} orders</span></Hero>}
          {totals.size === 0 ? <Card><Empty title="Nothing to cook yet" /></Card>
            : <Card>{[...totals.values()].map((t) => <Row key={`${t.name}|${t.unit}`} t={t.name} right={<Amt>{t.unit === 'kg' ? weight(t.qty) : `×${t.qty}`}</Amt>} />)}</Card>}
          {m.items.some((i) => i.stock !== null) && (
            <Group label="Limits">
              <Card pad style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {m.items.filter((i) => i.stock !== null).map((i) => (
                  <div key={i.id} className="p-stack" style={{ gap: 6 }}>
                    <div className="p-kv"><span>{i.name}</span><b>{i.unit === 'kg' ? `${weight(i.sold)} of ${weight(i.stock ?? 0)}` : `${i.sold} of ${i.stock}`}</b></div>
                    <Bar frac={(i.sold || 0) / Math.max(1, i.stock ?? 1)} />
                  </div>
                ))}
              </Card>
            </Group>
          )}
        </>
      )}

      {tab === 'pack' && (ready.length === 0 ? <Card><Empty title="Nothing to pack yet" /></Card> : (
        <>
        {portions.size > 0 && <Group label="By amount"><Card>{[...portions].map(([name, sizes]) =>
          <Row key={name} t={name} s={[...sizes].sort(([a], [b]) => a - b).map(([size, count]) => `${count} × ${weight(size)}`).join(' · ')} />
        )}</Card></Group>}
        <Group label="By customer">
          <Card>{ready.map((o) => <Row key={o.id} to={`/orders/${o.id}`} left={<Av name={o.name} />} t={`${o.name}${o.area ? ` · ${o.area}` : ''}`} s={o.lines.map(orderLineText).join(' · ')} right={<Badge>#{o.number}</Badge>} />)}</Card>
        </Group>
        </>
      ))}

      {tab === 'money' && (
        <>
          <Hero label="Collected"><Kick>Collected</Kick><b className="p-big">{formatInr(collected)}</b>
            <span className="p-muted">{formatInr(outstanding.reduce((sum, o) => sum + o.total, 0))} still to collect</span>
          </Hero>
          <Group label="Payments to check">
            <Card>{outstanding.length ? outstanding.map((o) => <Row key={o.id} to={`/orders/${o.id}`} left={<Av name={o.name} />}
              t={o.name} s={`#${o.number} · ${o.payState === 'says_paid' ? 'Says paid' : 'Unpaid'}`} right={<Amt>{formatInr(o.total)}</Amt>} />)
              : <Empty title="All clear">There are no outstanding payments for this Menu.</Empty>}</Card>
          </Group>
          {totals.size > 0 && <Group label="Items ordered"><Card>{[...totals.values()].map((item) =>
            <Row key={`${item.name}|${item.unit}`} t={item.name} right={<Amt>{item.unit === 'kg' ? weight(item.qty) : `×${item.qty}`}</Amt>} />
          )}</Card></Group>}
        </>
      )}

      {tab === 'share' && (
        <>
          <Card>
            <Hero style={{ borderRadius: 0, boxShadow: 'none', padding: '18px 20px', gap: 6 }} label="Link preview">
              <Kick>Order from the kitchen</Kick>
              <b style={{ fontSize: 18, letterSpacing: '-0.02em' }}>{m.name}</b>
              <span className="p-muted">{closesText(m.closesAt)} · delivery {daysText(m.days)}</span>
            </Hero>
            <Row t={<span style={{ fontWeight: 500, fontSize: 13, color: '#6A6359', wordBreak: 'break-all' }}>{link.replace(/^https?:\/\//, '')}</span>} right={<Sm label="Copy" onClick={() => void copy(link, setNote)} />} />
          </Card>
          <div className="p-msg">{postText}</div>
          <G2>
            <Btn2 label="Copy post" icon="sheet" onClick={() => void copy(postText, setNote)} />
            <Btn2 label="QR and sticker" icon="link" onClick={() => setSticker(true)} />
          </G2>
          <a className="p-link" href={link}>Open the customer page</a>
          {sticker && <Sheet label="Menu sticker" onClose={() => setSticker(false)}>
            <SheetHead title="Menu sticker" onClose={() => setSticker(false)} />
            <div className="p-menu-sticker">
              <h2>{m.name}</h2>
              <QRCodeSVG value={link} size={184} marginSize={4} title={`Scan to order from ${m.name}`} />
              <b>Scan to order</b><small>{link.replace(/^https?:\/\//, '')}</small>
            </div>
            <Btn label="Print sticker" onClick={() => window.print()} />
          </Sheet>}
          {note && <span className="p-status" role="status">{note}</span>}
          <Btn label="Post on WhatsApp" go="send" href={`https://wa.me/?text=${encodeURIComponent(postText)}`} />
        </>
      )}
      {openClose.error && <span className="p-error" role="alert">{errorMessage(openClose.error)}</span>}
    </Screen>
  );
}
